// src/components/ScreenerView.js
import React, { useEffect, useRef, useState } from "react";
import {
  getShadowEvaluationHistory,
  getLatestShadowRun,
  getScreenerResults,
  refreshScreener,
  snapshotReadinessGate,
  runScreenerScan,
  runShadowEvaluation,
  getFactorRankings,
} from "../api/screener";
import {
  getSettings,
  saveSettings,
  getScreenerPresets,
  saveScreenerPresets,
  getScreenerUniversePolicy,
  saveScreenerUniversePolicy,
  getScreenerViabilityPolicy,
  saveScreenerViabilityPolicy,
} from "../api/settings";
import { enterPaperTrade } from "../api/paperTrade";
import { useReadiness } from "../context/ReadinessContext";
import { Alert, Box, Button, Card, CardContent, Chip, FormControlLabel, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Tooltip, Typography } from "@mui/material";
import ResponsiveContainer from "./ResponsiveContainer";

const ScreenerView = () => {
  const { gate, history, alerts } = useReadiness();
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    minScore: 0,
    limit: 25,
    universeText: "",
    viabilityEnabled: true,
    minPrice: 5,
    minMarketCap: 1000000000,
    requireMarketCap: false,
  });
  const [shadowLatest, setShadowLatest] = useState(null);
  const [shadowHistory, setShadowHistory] = useState([]);
  const [shadowLoading, setShadowLoading] = useState(false);
  const [shadowError, setShadowError] = useState("");
  const [readinessGate, setReadinessGate] = useState(null);
  const [readinessHistory, setReadinessHistory] = useState([]);
  const [readinessAlerts, setReadinessAlerts] = useState([]);
  const [sortField, setSortField] = useState("rank");
  const [sortDir, setSortDir] = useState("asc");
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");
  const [factorRankings, setFactorRankings] = useState([]);
  const [scanDiagnostics, setScanDiagnostics] = useState({
    requestedUniverseCount: 0,
    eligibleUniverseCount: 0,
    rejectedCount: 0,
  });
  const [rejectedSymbols, setRejectedSymbols] = useState([]);
  const tableScrollRef = useRef(null);
  const topScrollRef = useRef(null);
  const syncingScrollRef = useRef(false);
  const [tableScrollMetrics, setTableScrollMetrics] = useState({ scrollWidth: 0, clientWidth: 0 });

  useEffect(() => {
    setReadinessGate(gate);
  }, [gate]);

  useEffect(() => {
    setReadinessHistory(Array.isArray(history) ? history : []);
  }, [history]);

  useEffect(() => {
    setReadinessAlerts(Array.isArray(alerts) ? alerts : []);
  }, [alerts]);

  const handleSortChange = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir(field === "rank" ? "asc" : "desc");
    }
  };

  const sortedResults = [...results].sort((a, b) => {
    const va = a?.[sortField];
    const vb = b?.[sortField];

    const bothNumbers = typeof va === "number" && typeof vb === "number";
    if (bothNumbers) {
      return sortDir === "asc" ? va - vb : vb - va;
    }

    const sa = String(va ?? "").toLowerCase();
    const sb = String(vb ?? "").toLowerCase();
    const cmp = sa.localeCompare(sb);
    return sortDir === "asc" ? cmp : -cmp;
  });

  const syncHorizontalMetrics = () => {
    const tableScroller = tableScrollRef.current;
    if (!tableScroller) {
      return;
    }
    const nextScrollWidth = Number(tableScroller.scrollWidth || 0);
    const nextClientWidth = Number(tableScroller.clientWidth || 0);
    setTableScrollMetrics((prev) => {
      if (prev.scrollWidth === nextScrollWidth && prev.clientWidth === nextClientWidth) {
        return prev;
      }
      return { scrollWidth: nextScrollWidth, clientWidth: nextClientWidth };
    });
  };

  const handleTopScrollerScroll = (event) => {
    if (syncingScrollRef.current) {
      return;
    }
    const tableScroller = tableScrollRef.current;
    if (!tableScroller) {
      return;
    }
    syncingScrollRef.current = true;
    tableScroller.scrollLeft = event.currentTarget.scrollLeft;
    requestAnimationFrame(() => {
      syncingScrollRef.current = false;
    });
  };

  const handleTableScrollerScroll = (event) => {
    if (syncingScrollRef.current) {
      return;
    }
    const topScroller = topScrollRef.current;
    if (!topScroller) {
      return;
    }
    syncingScrollRef.current = true;
    topScroller.scrollLeft = event.currentTarget.scrollLeft;
    requestAnimationFrame(() => {
      syncingScrollRef.current = false;
    });
  };

  const fetchResults = () => {
    setLoading(true);
    setError("");
    setSuccess("");
    Promise.all([
      getScreenerResults().catch(() => []),
      Promise.resolve().then(() => getFactorRankings()).catch(() => []),
    ])
      .then(async ([rows, rankings]) => {
        const initialRows = Array.isArray(rows) ? rows : [];
        let resolvedRows = initialRows;
        if (initialRows.length <= 2) {
          try {
            const parsedUniverse = String(filters.universeText || "")
              .split(",")
              .map((item) => item.trim().toUpperCase())
              .filter(Boolean);
            const scanResult = await runScreenerScan({
              ...filters,
              universe: parsedUniverse.length > 0 ? parsedUniverse : null,
            });
            const scanRows = Array.isArray(scanResult?.results) ? scanResult.results : [];
            if (scanRows.length > initialRows.length) {
              resolvedRows = scanRows;
              setRejectedSymbols(Array.isArray(scanResult?.rejectedSymbols) ? scanResult.rejectedSymbols : []);
              setScanDiagnostics({
                requestedUniverseCount: Number(scanResult?.requestedUniverseCount ?? 0),
                eligibleUniverseCount: Number(scanResult?.eligibleUniverseCount ?? 0),
                rejectedCount: Number(scanResult?.rejectedCount ?? 0),
              });
            }
          } catch {
            // Keep cached results if scan fallback path fails.
          }
        }
        setResults(resolvedRows);
        setFactorRankings(Array.isArray(rankings) ? rankings : []);
      })
      .catch(() => setError("Unable to load screener results. Please check your connection or try again."))
      .finally(() => setLoading(false));
  };

  const loadPreset = async () => {
    try {
      const [settings, universePolicy, viabilityPolicy] = await Promise.all([
        getSettings(),
        getScreenerUniversePolicy().catch(() => ({})),
        getScreenerViabilityPolicy().catch(() => ({})),
      ]);
      const universeSymbols = Array.isArray(universePolicy?.symbols) ? universePolicy.symbols : [];
      setFilters({
        minScore: Number(settings?.screener_min_score ?? 0),
        limit: Number(settings?.screener_limit ?? 25),
        universeText: universeSymbols.join(", "),
        viabilityEnabled: Boolean(viabilityPolicy?.enabled ?? true),
        minPrice: Number(viabilityPolicy?.min_price ?? 5),
        minMarketCap: Number(viabilityPolicy?.min_market_cap ?? 1000000000),
        requireMarketCap: Boolean(viabilityPolicy?.require_market_cap ?? false),
      });
    } catch {
      // Keep defaults if settings are unavailable.
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setError("");
    try {
      const rows = await refreshScreener();
      setResults(Array.isArray(rows) ? rows : []);
    } catch {
      setError("Unable to refresh screener results right now.");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRunScan = async () => {
    setIsRefreshing(true);
    setError("");
    setSuccess("");
    try {
      const parsedUniverse = String(filters.universeText || "")
        .split(",")
        .map((item) => item.trim().toUpperCase())
        .filter(Boolean);
      const scanPayload = {
        ...filters,
        universe: parsedUniverse.length > 0 ? parsedUniverse : null,
      };
      const scanResult = await runScreenerScan(scanPayload);
      setResults(Array.isArray(scanResult?.results) ? scanResult.results : []);
      setRejectedSymbols(Array.isArray(scanResult?.rejectedSymbols) ? scanResult.rejectedSymbols : []);
      setScanDiagnostics({
        requestedUniverseCount: Number(scanResult?.requestedUniverseCount ?? 0),
        eligibleUniverseCount: Number(scanResult?.eligibleUniverseCount ?? 0),
        rejectedCount: Number(scanResult?.rejectedCount ?? 0),
      });
    } catch {
      setError("Unable to run screener scan with current filters.");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSavePreset = async () => {
    setError("");
    setSuccess("");
    try {
      const current = await getSettings();
      const parsedUniverse = String(filters.universeText || "")
        .split(",")
        .map((item) => item.trim().toUpperCase())
        .filter(Boolean);

      const writes = [
        saveSettings({
          ...current,
          screener_min_score: Number(filters.minScore),
          screener_limit: Number(filters.limit),
        }),
        saveScreenerViabilityPolicy({
          enabled: Boolean(filters.viabilityEnabled),
          min_price: Number(filters.minPrice),
          min_market_cap: Number(filters.minMarketCap),
          require_market_cap: Boolean(filters.requireMarketCap),
        }),
      ];
      if (parsedUniverse.length > 0) {
        writes.push(saveScreenerUniversePolicy({ symbols: parsedUniverse }));
      }
      await Promise.all(writes);
      setSuccess("Screener preset saved.");
    } catch {
      setError("Unable to save screener preset.");
    }
  };

  const loadPresetsFromServer = async () => {
    try {
      const presetsData = await getScreenerPresets();
      setPresets(Array.isArray(presetsData) ? presetsData : []);
    } catch {
      // Keep existing presets if fetch fails
    }
  };

  const handleSaveNamedPreset = async () => {
    if (!presetName.trim()) {
      setError("Please enter a preset name.");
      return;
    }
    setError("");
    setSuccess("");
    try {
      const newPreset = {
        name: presetName,
        min_score: Number(filters.minScore),
        limit: Number(filters.limit),
      };
      const updatedPresets = [...presets, newPreset];
      await saveScreenerPresets(updatedPresets);
      setPresets(updatedPresets);
      setPresetName("");
      setSuccess(`Preset "${presetName}" saved successfully.`);
    } catch {
      setError("Unable to save preset.");
    }
  };

  const handleLoadPreset = (preset) => {
    setFilters({
      minScore: preset.min_score,
      limit: preset.limit,
    });
    setSuccess(`Preset "${preset.name}" loaded.`);
    setError("");
  };

  const loadShadowLatest = async () => {
    setShadowError("");
    try {
      const [payload, history] = await Promise.all([
        getLatestShadowRun(),
        getShadowEvaluationHistory(6),
      ]);
      setShadowLatest(payload?.has_latest ? payload.latest : null);
      setShadowHistory(Array.isArray(history) ? history : []);
    } catch {
      setShadowError("Unable to load prototype adoption evidence.");
    }
  };

  const handleSnapshotReadiness = async () => {
    setShadowLoading(true);
    setShadowError("");
    try {
      await snapshotReadinessGate();
      await loadShadowLatest();
    } catch {
      setShadowError("Unable to snapshot readiness gate right now.");
    } finally {
      setShadowLoading(false);
    }
  };

  const handleRunShadowEval = async () => {
    setShadowLoading(true);
    setShadowError("");
    try {
      const payload = await runShadowEvaluation({
        minScore: Number(filters.minScore),
        limit: Number(filters.limit),
        topN: Math.min(10, Number(filters.limit || 10)),
      });
      setShadowLatest(payload);
      await loadShadowLatest();
    } catch {
      setShadowError("Unable to run shadow evaluation right now.");
    } finally {
      setShadowLoading(false);
    }
  };

  const shadowDecision = shadowLatest?.evaluation?.decision;
  const shadowMetrics = shadowLatest?.evaluation?.metrics ?? null;
  const rejectionSummary = rejectedSymbols.reduce((acc, item) => {
    const reason = String(item?.reason || "filtered");
    acc[reason] = (acc[reason] || 0) + 1;
    return acc;
  }, {});
  const scoreHelpByField = {
    value_score: "Value score emphasizes medium-term price/value alignment and trend basis.",
    quality_score: "Quality score captures growth, profitability, and balance-sheet strength.",
    momentum_score: "Momentum score reflects short-term performance and technical confirmation.",
  };

  const [paperTradeStatus, setPaperTradeStatus] = useState({});

  const handlePaperTrade = async (row) => {
    const symbol = row.symbol;
    setPaperTradeStatus((prev) => ({ ...prev, [symbol]: "loading" }));
    try {
      await enterPaperTrade({
        symbol,
        entryPrice: Number(row.price ?? row.Price ?? 0),
        compositeScore: Number(row.composite_score ?? 0),
        valueScore: Number(row.value_score ?? 0),
        qualityScore: Number(row.quality_score ?? 0),
        momentumScore: Number(row.momentum_score ?? 0),
        notes: `screener scan ${new Date().toISOString()}`,
      });
      setPaperTradeStatus((prev) => ({ ...prev, [symbol]: "done" }));
    } catch {
      setPaperTradeStatus((prev) => ({ ...prev, [symbol]: "error" }));
    }
  };

  const handleExportRejectedCsv = () => {
    if (!Array.isArray(rejectedSymbols) || rejectedSymbols.length === 0) {
      return;
    }
    const header = ["symbol", "reason", "value"];
    const lines = [header.join(",")];
    for (const row of rejectedSymbols) {
      const values = [row?.symbol ?? "", row?.reason ?? "", row?.value ?? ""];
      lines.push(values.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "screener-rejected-symbols.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleFilterChange = (name) => (event) => {
    const isBool = event?.target?.type === "checkbox";
    const value = isBool ? event.target.checked : event.target.value;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    loadPreset();
    loadPresetsFromServer();
    fetchResults();
    loadShadowLatest();
  }, [retryCount]);

  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      syncHorizontalMetrics();
    });
    const tableScroller = tableScrollRef.current;
    if (!tableScroller) {
      return () => cancelAnimationFrame(rafId);
    }

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => {
        syncHorizontalMetrics();
      });
      observer.observe(tableScroller);
      if (tableScroller.firstElementChild) {
        observer.observe(tableScroller.firstElementChild);
      }
    }

    const onResize = () => syncHorizontalMetrics();
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [results, loading]);

  return (
    <ResponsiveContainer>
      <Card elevation={3} sx={{ width: "100%" }}>
        <CardContent>
          <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }} mb={2}>
            <Box>
              <Typography variant="h4" sx={{ fontSize: { xs: 22, sm: 28 } }}>
                Screener
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Ranked opportunities based on value, quality, and momentum factors.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={() => setRetryCount((c) => c + 1)}
                disabled={loading || isRefreshing}
              >
                Reload
              </Button>
              <Button variant="outlined" onClick={handleRunScan} disabled={loading || isRefreshing}>
                Run Scan
              </Button>
              <Button variant="outlined" onClick={handleSavePreset} disabled={loading || isRefreshing}>
                Save Preset
              </Button>
              <Button variant="contained" onClick={handleRefresh} disabled={loading || isRefreshing}>
                Refresh Scan
              </Button>
            </Stack>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
            <TextField
              type="number"
              size="small"
              label="Min Score"
              value={filters.minScore}
              onChange={handleFilterChange("minScore")}
            />
            <TextField
              type="number"
              size="small"
              label="Result Limit"
              value={filters.limit}
              onChange={handleFilterChange("limit")}
            />
            <TextField
              type="number"
              size="small"
              label="Min Price"
              value={filters.minPrice}
              onChange={handleFilterChange("minPrice")}
            />
            <TextField
              type="number"
              size="small"
              label="Min Market Cap"
              value={filters.minMarketCap}
              onChange={handleFilterChange("minMarketCap")}
            />
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2} sx={{ alignItems: "center" }}>
            <TextField
              type="text"
              size="small"
              label="Universe (comma-separated symbols)"
              value={filters.universeText}
              onChange={handleFilterChange("universeText")}
              placeholder="AAPL, MSFT, NVDA"
              sx={{ minWidth: { xs: "100%", sm: 420 } }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(filters.viabilityEnabled)}
                  onChange={handleFilterChange("viabilityEnabled")}
                />
              }
              label="Viability filters"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(filters.requireMarketCap)}
                  onChange={handleFilterChange("requireMarketCap")}
                  disabled={!Boolean(filters.viabilityEnabled)}
                />
              }
              label="Require market cap"
            />
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={3} sx={{ alignItems: "flex-start" }}>
            <TextField
              type="text"
              size="small"
              label="Preset Name"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="e.g., Conservative"
              sx={{ minWidth: 150 }}
            />
            <Button
              variant="outlined"
              onClick={handleSaveNamedPreset}
              disabled={loading || isRefreshing || !presetName.trim()}
            >
              Save as Preset
            </Button>
            {presets.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", alignItems: "center" }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: "auto" }}>
                  Presets:
                </Typography>
                {presets.map((preset) => (
                  <Chip
                    key={preset.name}
                    label={`${preset.name} (${preset.min_score}/${preset.limit})`}
                    onClick={() => handleLoadPreset(preset)}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Stack>
            )}
          </Stack>

          {loading && <Typography>Loading screener results...</Typography>}

          {!loading && error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          {!loading && success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {!loading && scanDiagnostics.requestedUniverseCount > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Universe screened: {scanDiagnostics.eligibleUniverseCount}/{scanDiagnostics.requestedUniverseCount}
              {scanDiagnostics.rejectedCount > 0 ? ` | rejected ${scanDiagnostics.rejectedCount}` : ""}
            </Alert>
          )}

          {!loading && rejectedSymbols.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Stack direction={{ xs: "column", sm: "row" }} sx={{ justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" } }} spacing={1} mb={0.75}>
                <Typography variant="subtitle2" color="text.secondary">
                  Rejected Symbols
                </Typography>
                <Button size="small" variant="outlined" onClick={handleExportRejectedCsv}>
                  Export Rejections CSV
                </Button>
              </Stack>
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", mb: 1 }}>
                {Object.entries(rejectionSummary).map(([reason, count]) => (
                  <Chip key={reason} size="small" color="warning" variant="filled" label={`${reason}: ${count}`} />
                ))}
              </Stack>
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap" }}>
                {rejectedSymbols.slice(0, 12).map((item, idx) => (
                  <Chip
                    key={`${item?.symbol || "symbol"}-${idx}`}
                    size="small"
                    variant="outlined"
                    color="warning"
                    label={`${item?.symbol || "?"}: ${item?.reason || "filtered"}`}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {!loading && results.length === 0 && !error && (
            <Typography color="text.secondary">
              No screener results found. Adjust filters or run a refresh scan.
            </Typography>
          )}

          {!loading && results.length > 0 && (
            <Box sx={{ width: "100%" }}>
              {tableScrollMetrics.scrollWidth > tableScrollMetrics.clientWidth && (
                <Box
                  ref={topScrollRef}
                  onScroll={handleTopScrollerScroll}
                  sx={{
                    width: "100%",
                    overflowX: "auto",
                    overflowY: "hidden",
                    mb: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "background.paper",
                  }}
                  aria-label="Horizontal column scrollbar"
                >
                  <Box sx={{ width: `${tableScrollMetrics.scrollWidth}px`, height: 14 }} />
                </Box>
              )}

              <TableContainer
                ref={tableScrollRef}
                onScroll={handleTableScrollerScroll}
                component={Paper}
                variant="outlined"
                sx={{ width: "100%", overflowX: "auto" }}
              >
                  <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "action.hover" }}>
                      {[
                        { id: "rank", label: "Rank" },
                        { id: "symbol", label: "Symbol", numeric: false },
                        { id: "composite_score", label: "Score" },
                        { id: "value_score", label: "Value" },
                        { id: "quality_score", label: "Quality" },
                        { id: "momentum_score", label: "Momentum" },
                        { id: "buy_ready", label: "Buy", numeric: false },
                        { id: "sell_ready", label: "Sell", numeric: false },
                        { id: "data_source", label: "Data", numeric: false },
                        { id: "data_completeness_pct", label: "Completeness" },
                        { id: "sleeve", label: "Sleeve", numeric: false },
                        { id: "risk_flags", label: "Flags", numeric: false },
                        { id: "paper_trade", label: "Paper Trade", numeric: false },
                      ].map((col) => (
                        <TableCell
                          key={col.id}
                          sortDirection={sortField === col.id ? sortDir : false}
                        >
                          <TableSortLabel
                            active={sortField === col.id}
                            direction={sortField === col.id ? sortDir : "asc"}
                            onClick={() => handleSortChange(col.id)}
                          >
                            <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                              <span>{col.label}</span>
                              {scoreHelpByField[col.id] && (
                                <Tooltip title={scoreHelpByField[col.id]} arrow>
                                  <Box
                                    component="span"
                                    aria-label={`${col.label} score info`}
                                    sx={{
                                      border: "1px solid",
                                      borderColor: "divider",
                                      borderRadius: "999px",
                                      width: 14,
                                      height: 14,
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "0.65rem",
                                      lineHeight: 1,
                                      color: "text.secondary",
                                    }}
                                  >
                                    ?
                                  </Box>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedResults.map((row) => (
                      <TableRow hover key={row.symbol}>
                        <TableCell>
                          <Chip size="small" label={`#${row.rank}`} color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{row.symbol}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={Number(row.composite_score ?? 0).toFixed(2)}
                            color={
                              row.composite_score >= 60 ? "success" :
                              row.composite_score >= 35 ? "warning" : "default"
                            }
                            variant="filled"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip size="small" variant="outlined"
                            color={Number(row.value_score ?? 0) >= 8 ? "success" : Number(row.value_score ?? 0) >= 3 ? "warning" : "default"}
                            label={Number(row.value_score ?? 0).toFixed(1)}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip size="small" variant="outlined"
                            color={Number(row.quality_score ?? 0) >= 12 ? "success" : Number(row.quality_score ?? 0) >= 5 ? "warning" : "default"}
                            label={Number(row.quality_score ?? 0).toFixed(1)}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip size="small" variant="outlined"
                            color={Number(row.momentum_score ?? 0) >= 15 ? "success" : Number(row.momentum_score ?? 0) >= 5 ? "warning" : "default"}
                            label={Number(row.momentum_score ?? 0).toFixed(1)}
                          />
                        </TableCell>
                        <TableCell>
                          {row.buy_ready != null
                            ? <Chip size="small" label={row.buy_ready ? "BUY" : "—"} color={row.buy_ready ? "success" : "default"} variant={row.buy_ready ? "filled" : "outlined"} />
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {row.sell_ready != null
                            ? <Chip size="small" label={row.sell_ready ? "SELL" : "—"} color={row.sell_ready ? "error" : "default"} variant={row.sell_ready ? "filled" : "outlined"} />
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                            <Chip
                              size="small"
                              label={row.data_source ? String(row.data_source).toUpperCase() : "UNKNOWN"}
                              color={
                                row.data_source === "live"
                                  ? "success"
                                  : row.data_source === "fallback"
                                    ? "warning"
                                    : "default"
                              }
                              variant={row.data_source === "stub" ? "outlined" : "filled"}
                            />
                            {Array.isArray(row.missing_fields) && row.missing_fields.length > 0 && (
                              <Chip
                                size="small"
                                variant="outlined"
                                color="default"
                                label={`missing ${Math.min(row.missing_fields.length, 8)}`}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={
                              Number(row.data_completeness_pct ?? 0) >= 75
                                ? "success"
                                : Number(row.data_completeness_pct ?? 0) >= 35
                                  ? "warning"
                                  : "default"
                            }
                            label={`${Number(row.data_completeness_pct ?? 0).toFixed(1)}%`}
                          />
                        </TableCell>
                        <TableCell>
                          {row.sleeve
                            ? <Chip size="small" label={row.sleeve} variant="outlined" color={row.sleeve === "long_term" ? "info" : row.sleeve === "short_term" ? "secondary" : row.sleeve === "balanced" ? "success" : "default"} />
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {Array.isArray(row.risk_flags) && row.risk_flags.length > 0
                            ? row.risk_flags.map((f) => (
                                <Chip key={f} size="small" label={f} color="warning" variant="outlined" sx={{ mr: 0.5, fontSize: "0.65rem" }} />
                              ))
                            : <Chip size="small" label="clean" color="success" variant="outlined" />}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            color={paperTradeStatus[row.symbol] === "done" ? "success" : paperTradeStatus[row.symbol] === "error" ? "error" : "primary"}
                            disabled={paperTradeStatus[row.symbol] === "loading" || paperTradeStatus[row.symbol] === "done"}
                            onClick={() => handlePaperTrade(row)}
                            aria-label={`Paper trade ${row.symbol}`}
                          >
                            {paperTradeStatus[row.symbol] === "done" ? "Entered" : paperTradeStatus[row.symbol] === "error" ? "Failed" : "Paper Trade"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  </Table>
              </TableContainer>
            </Box>
          )}

          <Card variant="outlined" sx={{ mt: 3 }}>
            <CardContent>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                sx={{ justifyContent: "space-between" }}
                spacing={1}
                mb={1}
              >
                <Box>
                  <Typography variant="h6">Prototype Adoption Evidence</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Shadow-only baseline vs candidate comparison. Live screener behavior is unchanged.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" onClick={loadShadowLatest} disabled={shadowLoading}>
                    Refresh Evidence
                  </Button>
                  <Button variant="outlined" onClick={handleSnapshotReadiness} disabled={shadowLoading}>
                    Snapshot Readiness
                  </Button>
                  <Button variant="contained" onClick={handleRunShadowEval} disabled={shadowLoading}>
                    Run Shadow Eval
                  </Button>
                </Stack>
              </Stack>

              {shadowError && <Alert severity="error" sx={{ mb: 1 }}>{shadowError}</Alert>}

              {readinessGate && (
                <Alert severity={readinessGate.ready ? "success" : "warning"} sx={{ mb: 1 }}>
                  Promotion Readiness: {readinessGate.ready ? "READY" : "NOT READY"} ({Number(readinessGate.score_pct ?? 0).toFixed(1)}%)
                  {!readinessGate.ready && readinessGate.blockers?.length > 0
                    ? ` | blockers: ${readinessGate.blockers.join(", ")}`
                    : ""}
                </Alert>
              )}

              {!shadowLatest && !shadowError && (
                <Typography color="text.secondary">No shadow evaluation run has been recorded yet.</Typography>
              )}

              {shadowLatest && (
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <Typography variant="body2">Decision:</Typography>
                    <Chip
                      size="small"
                      color={shadowDecision === "promote_candidate" ? "success" : "default"}
                      label={shadowDecision || "unknown"}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {shadowLatest?.evaluation?.timestamp || shadowLatest?.timestamp || ""}
                    </Typography>
                  </Stack>

                  {shadowMetrics && (
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <Typography variant="body2">
                        Top-N Overlap: {Number(shadowMetrics.top_n_overlap_ratio ?? 0).toFixed(4)}
                      </Typography>
                      <Typography variant="body2">
                        Mean Score Delta: {Number(shadowMetrics.mean_abs_score_delta ?? 0).toFixed(4)}
                      </Typography>
                      <Typography variant="body2">
                        API Call Reduction: {Number(shadowMetrics.api_call_reduction_pct ?? 0).toFixed(2)}%
                      </Typography>
                      <Typography variant="body2">
                        Quality Delta: {Number(shadowMetrics.quality_delta ?? 0).toFixed(4)}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              )}

              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Recent Readiness Snapshots
                </Typography>
                {readinessHistory.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    No readiness snapshots recorded yet.
                  </Typography>
                ) : (
                  <Stack spacing={0.75} mb={1.5}>
                    {readinessHistory.slice().reverse().map((item, idx) => (
                      <Stack
                        key={`${item?.as_of || "readiness"}-${idx}`}
                        direction={{ xs: "column", sm: "row" }}
                        sx={{ justifyContent: "space-between", borderBottom: "1px solid", borderColor: "divider", pb: 0.75 }}
                      >
                        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                          <Chip
                            size="small"
                            color={item?.ready ? "success" : "default"}
                            label={item?.ready ? "READY" : "NOT READY"}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {item?.as_of || ""}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          score {Number(item?.score_pct ?? 0).toFixed(1)}% | blockers {(item?.blockers || []).length}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}

                <Typography variant="subtitle2" gutterBottom>
                  Recent Readiness Alerts
                </Typography>
                {readinessAlerts.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    No readiness alerts emitted yet.
                  </Typography>
                ) : (
                  <Stack spacing={0.75} mb={1.5}>
                    {readinessAlerts.slice().reverse().map((item, idx) => (
                      <Stack
                        key={`${item?.timestamp || "alert"}-${idx}`}
                        direction={{ xs: "column", sm: "row" }}
                        sx={{ justifyContent: "space-between", borderBottom: "1px solid", borderColor: "divider", pb: 0.75 }}
                      >
                        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                          <Chip
                            size="small"
                            color={item?.event_type === "readiness_recovered" ? "success" : "warning"}
                            label={item?.event_type || "readiness_event"}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {item?.timestamp || ""}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          score {Number(item?.score_pct ?? 0).toFixed(1)}% | blockers {(item?.blockers || []).length}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}

                <Typography variant="subtitle2" gutterBottom>
                  Recent Shadow Decisions
                </Typography>
                {shadowHistory.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No historical shadow records yet.
                  </Typography>
                ) : (
                  <Stack spacing={0.75}>
                    {shadowHistory.slice().reverse().map((item, idx) => {
                      const metrics = item?.metrics ?? {};
                      const decision = item?.decision || "unknown";
                      return (
                        <Stack
                          key={`${item?.timestamp || "entry"}-${idx}`}
                          direction={{ xs: "column", sm: "row" }}
                          sx={{ justifyContent: "space-between", borderBottom: "1px solid", borderColor: "divider", pb: 0.75 }}
                        >
                          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                            <Chip
                              size="small"
                              color={decision === "promote_candidate" ? "success" : "default"}
                              label={decision}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {item?.timestamp || ""}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            overlap {Number(metrics.top_n_overlap_ratio ?? 0).toFixed(3)} | api save {Number(metrics.api_call_reduction_pct ?? 0).toFixed(1)}%
                          </Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </CardContent>
          </Card>
        </CardContent>
      </Card>

      {factorRankings.length > 0 && (
        <Card elevation={2} sx={{ mt: 2, width: "100%" }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Factor Rankings
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Symbol</TableCell>
                    <TableCell align="right">Score</TableCell>
                    <TableCell>Rank</TableCell>
                    {factorRankings[0] && Object.keys(factorRankings[0])
                      .filter((k) => !["symbol", "score", "rank"].includes(k))
                      .map((k) => <TableCell key={k} align="right">{k}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {factorRankings.map((row, i) => (
                    <TableRow key={row.symbol ?? i} hover>
                      <TableCell><strong>{row.symbol ?? "--"}</strong></TableCell>
                      <TableCell align="right">{row.score != null ? Number(row.score).toFixed(2) : "--"}</TableCell>
                      <TableCell>{row.rank ?? "--"}</TableCell>
                      {Object.keys(factorRankings[0])
                        .filter((k) => !["symbol", "score", "rank"].includes(k))
                        .map((k) => (
                          <TableCell key={k} align="right">
                            {row[k] != null ? (typeof row[k] === "number" ? Number(row[k]).toFixed(2) : String(row[k])) : "--"}
                          </TableCell>
                        ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </ResponsiveContainer>
  );
};

export default ScreenerView;
